import { convertSbPathToUrl } from "./convertSbPathToUrl";
import type { StoryblokSchemaContext } from "./storyblokSchemaContext";

/** A link field's rendered URL when it points nowhere. */
export const EMPTY_LINK_URL = "#";

/**
 * A Storyblok link as it actually arrives, before the schema turns it into a
 * `StoryblokLink`. Everything is optional because every part of it is: an
 * emptied link field still comes back as an object, just a blank one.
 *
 * `story` is the payload `resolve_links: "url"` produces — see
 * `storyblokCdnClient`, which explains why it's the client rather than the CDN
 * that puts it here, and why it's absent more often than you'd expect.
 */
export type RawStoryblokLink = {
  linktype?: string | null;
  url?: string | null;
  email?: string | null;
  cached_url?: string | null;
  story?: { full_slug?: string | null } | null;
};

/**
 * The finished frontend URL for a link field, backing `StoryblokLink.url`.
 *
 * This is the whole reason the schema exposes one link type instead of a
 * variant per `linktype`: every case below collapses to a URL string here, so
 * a consumer never branches on which kind of link it got, and turning on a
 * field's email/asset link types in Storyblok can't break a call site that
 * didn't anticipate them.
 *
 * Internal links prefer the resolved `story.full_slug` and fall back to
 * `cached_url`. Both normalise to the same result — `convertSbPathToUrl`
 * matches from the `energydrink`/`crh`/`questions` segment onward and discards
 * anything before it, so the language prefix `full_slug` carries under a
 * `language` request makes no difference — but `full_slug` is the value
 * Storyblok actually resolved, and `cached_url` is only a cache of what the
 * path was when the link was saved.
 *
 * Lives in its own module, rather than beside the resolvers that use it, so it
 * can be tested without dragging in `storyblokCdnClient` — which is
 * `server-only` and eagerly validates the app's full env.
 */
export function resolveLinkUrl(
  link: RawStoryblokLink | null | undefined,
  context: StoryblokSchemaContext,
): string {
  if (!link) return EMPTY_LINK_URL;

  switch (link.linktype) {
    case "email":
      return link.email ? `mailto:${link.email}` : EMPTY_LINK_URL;

    // An asset link is already a full CDN URL, as is an external one.
    case "asset":
    case "url":
      return link.url || EMPTY_LINK_URL;

    // "story" — and anything unrecognised, which an emptied field can be:
    // it comes back with no `linktype` at all.
    default: {
      const path = link.story?.full_slug || link.cached_url;
      if (!path) return EMPTY_LINK_URL;

      try {
        return convertSbPathToUrl(context.language, path);
      } catch {
        // `convertSbPathToUrl` throws on a path outside the routed trees — an
        // editor linking to a story the frontend doesn't serve. Degrading to
        // the placeholder keeps that a dead link instead of failing the whole
        // query: resolution used to happen per-component at render time, so
        // one bad link only broke its own component, whereas a thrown field
        // error here would take the entire page's data with it.
        console.warn(
          `[storyblok-schema] Link to unroutable path "${path}" — rendering as "${EMPTY_LINK_URL}".`,
        );
        return EMPTY_LINK_URL;
      }
    }
  }
}
